import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  Container,
  Paper,
  Stack,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import SEO from '../components/SEO';
import LazyImage from "../components/LazyImage"; // Add this import
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SwordSlashAnim from "../components/SwordSlashAnim/SwordSlashAnim";

const BUCKET_URL = "https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/logos/";

const CharacterVoting = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [selectedPower, setSelectedPower] = useState(null);
  const [selectedPopularity, setSelectedPopularity] = useState(null);

  // Filter state
  const [mangaTags, setMangaTags] = useState([]);
  const [selectedScope, setSelectedScope] = useState("Global");
  const [selectedManga, setSelectedManga] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [mangaDialogOpen, setMangaDialogOpen] = useState(false);
  const [slashIdx, setSlashIdx] = useState(null);

  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(
    "(max-width:600px) and (orientation: portrait)"
  );

  // Fetch manga tags (category = 'manga')
  useEffect(() => {
    const fetchMangaTags = async () => {
      const { data } = await supabase
        .from("leaderboard_tags")
        .select("tag, description, category, logo_url") // <-- Add logo_url
        .eq("category", "manga");
      setMangaTags(data || []);
    };
    fetchMangaTags();
  }, []);

  // Fetch characters based on filter
  const fetchCharacters = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsVoting(true);
    }
    
    let query = supabase
      .from("characters")
      .select("id, name, image_url, manga, character_tag_scores(id, tag, score, wins, losses, type), gender, role");

    // Apply filters
    if (selectedScope === "Manga" && selectedManga) {
      query = query.eq("manga", selectedManga);
    } else if (selectedScope === "CharacterType" && selectedRole) {
      if (selectedRole === "Waifus") {
        query = query.eq("gender", "Female");
      } else {
        query = query.eq("role", selectedRole);
      }
    }

    const { data } = await query;
    if (data && data.length >= 2) {
      // Shuffle and pick two random characters
      const shuffled = [...data].sort(() => Math.random() - 0.5);
      setCharacters([shuffled[0], shuffled[1]]);
    } else {
      setCharacters([]);
    }
    setSelectedPower(null);
    setSelectedPopularity(null);
    setLoading(false);
    setIsVoting(false);
  };

  useEffect(() => {
    fetchCharacters(true);
    // eslint-disable-next-line
  }, [selectedScope, selectedManga, selectedRole]);

  // ELO voting logic for tags and type, only for shared tags
  const handleVote = async (winnerIdx, loserIdx, type) => {
    const winner = characters[winnerIdx];
    const loser = characters[loserIdx];

    const winnerScores = (winner.character_tag_scores || []).filter(s => s.type === type);
    const loserScores = (loser.character_tag_scores || []).filter(s => s.type === type);

    // Find shared tags for the selected type
    const sharedTags = winnerScores
      .map(s => s.tag)
      .filter(tag => loserScores.some(ls => ls.tag === tag));

    for (const tag of sharedTags) {
      const winnerTagScore = winnerScores.find((s) => s.tag === tag);
      const loserTagScore = loserScores.find((s) => s.tag === tag);

      if (winnerTagScore && loserTagScore) {
        // Calculate new ELOs
        const [newWinnerElo, newLoserElo] = calculateElo(
          winnerTagScore.score,
          loserTagScore.score
        );

        // Update winner's score and wins
        await supabase
          .from("character_tag_scores")
          .update({
            score: newWinnerElo,
            wins: (winnerTagScore.wins || 0) + 1,
          })
          .eq("id", winnerTagScore.id);

        // Update loser's score and losses
        await supabase
          .from("character_tag_scores")
          .update({
            score: newLoserElo,
            losses: (loserTagScore.losses || 0) + 1,
          })
          .eq("id", loserTagScore.id);
      }
    }

    // Play sword slash animation ONLY for power_level votes
    if (type === "power_level") {
      setSlashIdx(loserIdx);
      setTimeout(() => setSlashIdx(null), 600); // Hide animation after 0.6s
    }
  };

  // ELO calculation function
  function calculateElo(winnerElo, loserElo, k = 32) {
    const expectedWin = 1 / (1 + Math.pow(10, (loserElo - winnerElo) / 400));
    const expectedLose = 1 / (1 + Math.pow(10, (winnerElo - loserElo) / 400));
    const newWinnerElo = Math.round(winnerElo + k * (1 - expectedWin));
    const newLoserElo = Math.round(loserElo + k * (0 - expectedLose));
    return [newWinnerElo, newLoserElo];
  }

  // Allow confirm if at least one is selected
  const canConfirm = selectedPower !== null || selectedPopularity !== null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    
    setIsVoting(true);
    
    // Power Level vote
    if (selectedPower !== null) {
      await handleVote(selectedPower, 1 - selectedPower, "power_level");
    }
    // Popularity vote
    if (selectedPopularity !== null) {
      await handleVote(selectedPopularity, 1 - selectedPopularity, "popularity");
    }
    
    // Increment solo_versus_votes for the logged in user
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // First get the current value
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('solo_versus_votes')
        .eq('id', user.id)
        .single();
    
      if (!fetchError && profile) {
        // Then update with incremented value
        const { error } = await supabase
          .from('profiles')
          .update({ 
            solo_versus_votes: (profile.solo_versus_votes || 0) + 1 
          })
          .eq('id', user.id);
        
        if (error) {
          console.error('Error updating solo_versus_votes:', error);
        }
      }
    }
    
    await fetchCharacters(false);
  };

  const handleSkip = async () => {
    setIsVoting(true);
    await fetchCharacters(false);
  };

  const handlePowerSelect = (idx) => {
    setSelectedPower(idx === selectedPower ? null : idx);
  };

  const handlePopularitySelect = (idx) => {
    setSelectedPopularity(idx === selectedPopularity ? null : idx);
  };

  // Handler for character type buttons
  const handleRoleSelect = (role) => {
    if (selectedScope === "CharacterType" && selectedRole === role) {
      setSelectedScope("Global");
      setSelectedRole(null);
      setSelectedManga(null);
    } else {
      setSelectedScope("CharacterType");
      setSelectedRole(role);
      setSelectedManga(null);
    }
  };

  // Handler for scope selection
  const handleScopeSelect = (scope) => {
    if (scope === "Global") {
      setSelectedScope("Global");
      setSelectedRole(null);
      setSelectedManga(null);
    } else if (scope === "Manga") {
      setMangaDialogOpen(true);
    }
  };

  // Create unique key for matchup to trigger fade transitions
  const matchupKey = !loading && characters.length >= 2
    ? characters.map(c => c?.id).join("-")
    : "loading";

  return (
    <>
      <SEO 
        title="Vote on Anime Character Battles & Power Scaling"
        description="Vote on anime vs battles and power scaling matchups. Compare character strength and popularity in head-to-head battles. Join the ultimate anime powerscaling community!"
        keywords="anime vs battles, anime character voting, power scaling battles, anime strength comparison, character battle voting"
        url="/character-voting"
      />
      <Box sx={{ minHeight: "100vh" }}> {/* Removed background property */}
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />
        
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 10 } }}>
          {/* Title */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 40, sm: 56, md: 68 },
              fontWeight: 900,
              background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              mb: 6,
              letterSpacing: 2,
            }}
          >
            SOLO VERSUS
          </Typography>

          {/* Filter Controls Section */}
          <Paper
            sx={{
              background: "rgba(35, 35, 35, 0.9)", // Made transparent
              borderRadius: 4,
              p: 4,
              mb: 6,
              border: "2px solid #3e3e3e",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(10px)", // Add glass effect
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#fff",
                mb: 4,
                textAlign: "center",
                fontSize: { xs: 24, sm: 32 },
              }}
            >
              Choose Your Battle Scope
            </Typography>

            {/* Scope Selection */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 4, justifyContent: "center" }}>
              <Button
                variant={selectedScope === "Global" ? "contained" : "outlined"}
                onClick={() => handleScopeSelect("Global")}
                sx={{
                  height: 80,
                  minWidth: 200,
                  borderRadius: 3,
                  fontSize: { xs: 18, sm: 24 },
                  fontWeight: 700,
                  px: 4,
                  background: selectedScope === "Global" 
                    ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                    : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                  color: "#fff",
                  border: selectedScope === "Global" ? "none" : "2px solid #555",
                  boxShadow: selectedScope === "Global" 
                    ? "0 4px 20px rgba(99, 102,241, 0.4)"
                    : "none",
                  "&:hover": {
                    background: selectedScope === "Global"
                      ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                      : "rgba(99, 102,241, 0.1)",
                    borderColor: "#6366f1",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Global
              </Button>
              
              {selectedScope === "Manga" && selectedManga ? (
                <Button
                  onClick={() => setMangaDialogOpen(true)}
                  sx={{
                    height: 80,
                    minWidth: 200,
                    borderRadius: 3,
                    p: 0,
                    background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
                    boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)",
                    "&:hover": { 
                      background: "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    component="img"
                    src={`${BUCKET_URL}${selectedManga}_logo.webp`}
                    alt={selectedManga}
                    sx={{
                      height: 60,
                      width: 140,
                      aspectRatio: "21/9",
                      objectFit: "contain",
                      background: "rgba(17, 17, 17, 0.8)",
                      borderRadius: 2,
                    }}
                  />
                </Button>
              ) : (
                <Button
                  variant={selectedScope === "Manga" ? "contained" : "outlined"}
                  onClick={() => handleScopeSelect("Manga")}
                  sx={{
                    height: 80,
                    minWidth: 200,
                    borderRadius: 3,
                    fontSize: { xs: 18, sm: 24 },
                    fontWeight: 700,
                    px: 4,
                    background: selectedScope === "Manga" 
                      ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                      : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                    color: "#fff",
                    border: selectedScope === "Manga" ? "none" : "2px solid #555",
                    boxShadow: selectedScope === "Manga" 
                      ? "0 4px 20px rgba(99, 102, 241, 0.4)"
                      : "none",
                    "&:hover": {
                      background: selectedScope === "Manga"
                        ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                        : "rgba(99, 102,241, 0.1)",
                      borderColor: "#6366f1",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Manga
                </Button>
              )}
            </Stack>

            {/* Character Type Filters */}
            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={2} 
              sx={{ justifyContent: "center" }}
            >
              {[{
                role: "Main Character",
                label: "Main Characters",
                icon: "⭐",
              },
              {
                role: "Antagonist",
                label: "Antagonists",
                icon: "💀",
              },
              {
                role: "Waifus",
                label: "Waifus",
                icon: "💖",
              }].map(({ role, label, icon }) => (
                <Button
                  key={role}
                  variant={selectedScope === "CharacterType" && selectedRole === role ? "contained" : "outlined"}
                  onClick={() => handleRoleSelect(role)}
                  sx={{
                    height: 60,
                    minWidth: 160,
                    borderRadius: 3,
                    fontSize: { xs: 16, sm: 18 },
                    fontWeight: 700,
                    px: 3,
                    background: selectedScope === "CharacterType" && selectedRole === role
                      ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                      : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                    color: "#fff",
                    border: selectedScope === "CharacterType" && selectedRole === role ? "none" : "2px solid #555",
                    boxShadow: selectedScope === "CharacterType" && selectedRole === role
                      ? "0 4px 16px rgba(99, 102,241, 0.3)"
                      : "none",
                    "&:hover": {
                      background: selectedScope === "CharacterType" && selectedRole === role
                        ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                        : "rgba(99, 102, 241, 0.1)",
                      borderColor: "#6366f1",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {icon} {label}
                </Button>
              ))}
            </Stack>
          </Paper>

          {/* Manga Selection Dialog */}
          <Dialog 
            open={mangaDialogOpen} 
            onClose={() => setMangaDialogOpen(false)}
            PaperProps={{
              sx: { 
                background: "rgba(35, 35, 35, 0.95)", 
                color: "#fff",
                backdropFilter: "blur(10px)",
                border: "2px solid #f49330",
                borderRadius: 4,
              }
            }}
          >
            <Paper sx={{ 
              background: "rgba(35, 35, 35, 0.9)", // Made transparent
              color: "#fff",
              backdropFilter: "blur(10px)", // Add glass effect
              WebkitBackdropFilter: "blur(10px)",
            }}>
              <DialogTitle 
                sx={{ 
                  textAlign: "center", 
                  fontWeight: 700, 
                  fontSize: 32,
                  pb: 2,
                  background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Choose a Manga
              </DialogTitle>
              <Box sx={{ p: 4 }}>
                <Grid container spacing={3} justifyContent="center">
                  {mangaTags.map((manga) => (
                    <Grid item xs={6} sm={4} md={3} key={manga.tag}>
                      <Button
                        onClick={() => {
                          setSelectedScope("Manga");
                          setSelectedManga(manga.tag);
                          setMangaDialogOpen(false);
                        }}
                        sx={{
                          width: "100%",
                          p: 0,
                          borderRadius: 3,
                          overflow: "hidden",
                          background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                          border: "2px solid #3e3e3e",
                          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
                            borderColor: "#6366f1",
                          },
                        }}
                      >
                        <img
                          src={manga.logo_url || `${BUCKET_URL}${manga.tag}_logo.webp`}
                          alt={manga.tag}
                          style={{
                            width: "100%",
                            aspectRatio: "21/9",
                            objectFit: "contain",
                            background: "#111",
                            padding: 12,
                            borderRadius: 8,
                          }}
                          onError={e => { e.target.style.display = "none"; }}
                        />
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Dialog>

          {/* Voting Area */}
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 600 }}>
                Loading characters...
              </Typography>
            </Box>
          ) : characters.length < 2 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 600 }}>
                Not enough characters for this filter...
              </Typography>
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={matchupKey}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 6,
                    mb: 6,
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                  }}
                >
                  {characters.map((char, idx) =>
                    isMobilePortrait ? (
                      // --- MOBILE PORTRAIT LAYOUT ---
                      <Paper
                        key={char.id}
                        sx={{
                          background: "rgba(35, 35, 35, 0.9)",
                          borderRadius: 4,
                          p: 0,
                          width: "100%",
                          maxWidth: 380,
                          minHeight: 140,
                          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                          border: "2px solid #3e3e3e",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          WebkitBackdropFilter: "blur(10px)",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "stretch",
                          overflow: "hidden",
                          mb: 2,
                        }}
                      >
                        {/* Image on the left, 9:16 aspect */}
                        <Box
                          sx={{
                            position: "relative", // <-- Add position relative
                            width: 90,
                            minWidth: 90,
                            aspectRatio: "9/16",
                            height: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#181818",
                            flexShrink: 0,
                            overflow: "hidden",
                          }}
                        >
                          <LazyImage
                            src={char.image_url}
                            alt={char.name}
                            threshold={0.2}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: 0,
                              display: "block",
                            }}
                          />
                          {slashIdx === idx && <SwordSlashAnim />}
                        </Box>
                        {/* Text and buttons on the right */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            px: 2,
                            py: 2,
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              fontSize: 16,
                              width: "100%",
                              whiteSpace: "normal",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "left",
                              mb: 0.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {char.name}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#6366f1",
                              fontSize: 13,
                              fontWeight: 600,
                              width: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "left",
                              mb: 1,
                            }}
                          >
                            {char.manga}
                          </Typography>
                          <Button
                            disabled={isVoting}
                            onClick={() => handlePowerSelect(idx)}
                            sx={{
                              width: "100%",
                              py: 1.1,
                              fontSize: 14,
                              fontWeight: 600,
                              background:
                                selectedPower === idx
                                  ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                                  : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                              color: selectedPower === idx ? "#181818" : "#fff",
                              border: selectedPower === idx ? "none" : "2px solid #555",
                              borderRadius: 3,
                              "&:hover": {
                                background:
                                  selectedPower === idx
                                    ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)"
                                    : "rgba(244, 147, 48, 0.1)",
                                borderColor: "#f49330",
                              },
                              "&:disabled": {
                                opacity: 0.7,
                              },
                              transition: "all 0.3s ease",
                              mb: 1,
                            }}
                          >
                            {selectedPower === idx
                              ? "✓ Selected as Stronger"
                              : "Vote Stronger"}
                          </Button>
                          <Button
                            disabled={isVoting}
                            onClick={() => handlePopularitySelect(idx)}
                            sx={{
                              width: "100%",
                              py: 1.1,
                              fontSize: 14,
                              fontWeight: 600,
                              background:
                                selectedPopularity === idx
                                  ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                                  : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                              color: "#fff",
                              border: selectedPopularity === idx ? "none" : "2px solid #555",
                              borderRadius: 3,
                              "&:hover": {
                                background:
                                  selectedPopularity === idx
                                    ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                                    : "rgba(99, 102, 241, 0.1)",
                                borderColor: "#6366f1",
                              },
                              "&:disabled": {
                                opacity: 0.7,
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {selectedPopularity === idx
                              ? "✓ Selected as Favorite"
                              : "Vote Favorite"}
                          </Button>
                        </Box>
                      </Paper>
                    ) : (
                      // --- ORIGINAL LAYOUT FOR ALL OTHER CASES ---
                      <Paper
                        key={char.id}
                        sx={{
                          background: "rgba(35, 35, 35, 0.9)",
                          borderRadius: 4,
                          p: 4,
                          width: { xs: "100%", md: 380 },
                          maxWidth: 380,
                          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                          border: "2px solid #3e3e3e",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          WebkitBackdropFilter: "blur(10px)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#6366f1",
                            boxShadow: "0 12px 40px rgba(99, 102, 241, 0.2)",
                            transform: "translateY(-4px)",
                          },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Box sx={{ textAlign: "center", mb: 3, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Box sx={{ position: "relative", width: 200, height: 320, mb: 2 }}>
                            <LazyImage
                              src={char.image_url}
                              alt={char.name}
                              threshold={0.2}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: 3,
                                border: "2px solid #444",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                                display: "block",
                                mx: "auto",
                              }}
                            />
                            {slashIdx === idx && <SwordSlashAnim />}
                          </Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "#fff" }}>
                            {char.name}
                          </Typography>
                          <Typography sx={{ color: "#6366f1", fontSize: 16, fontWeight: 600 }}>
                            {char.manga}
                          </Typography>
                        </Box>
                        <Stack spacing={2} sx={{ width: "100%" }}>
                          <Button
                            disabled={isVoting}
                            onClick={() => handlePowerSelect(idx)}
                            sx={{
                              width: "100%",
                              py: 2,
                              fontSize: 16,
                              fontWeight: 600,
                              background: selectedPower === idx
                                ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                                : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                              color: selectedPower === idx ? "#181818" : "#fff",
                              border: selectedPower === idx ? "none" : "2px solid #555",
                              borderRadius: 3,
                              "&:hover": {
                                background: selectedPower === idx
                                  ? "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)"
                                  : "rgba(244, 147, 48, 0.1)",
                                borderColor: "#f49330",
                              },
                              "&:disabled": {
                                opacity: 0.7,
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {selectedPower === idx ? "✓ Selected as Stronger" : "Vote Stronger"}
                          </Button>
                          <Button
                            disabled={isVoting}
                            onClick={() => handlePopularitySelect(idx)}
                            sx={{
                              width: "100%",
                              py: 2,
                              fontSize: 16,
                              fontWeight: 600,
                              background: selectedPopularity === idx
                                ? "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)"
                                : "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                              color: "#fff",
                              border: selectedPopularity === idx ? "none" : "2px solid #555",
                              borderRadius: 3,
                              "&:hover": {
                                background: selectedPopularity === idx
                                  ? "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)"
                                  : "rgba(99, 102, 241, 0.1)",
                                borderColor: "#6366f1",
                              },
                              "&:disabled": {
                                opacity: 0.7,
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            {selectedPopularity === idx ? "✓ Selected as Favorite" : "Vote Favorite"}
                          </Button>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Box>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4 }}>
            <Button
              onClick={handleSkip}
              disabled={isVoting}
              sx={{
                px: 6,
                py: 2,
                fontSize: 18,
                fontWeight: 700,
                borderRadius: 3,
                background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                color: "#fff",
                border: "2px solid #555",
                "&:hover": {
                  background: "rgba(244, 147, 48, 0.1)",
                  borderColor: "#f49330",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  opacity: 0.7,
                  background: "#444",
                  borderColor: "#666",
                },
                transition: "all 0.3s ease",
              }}
            >
              {isVoting ? "Loading..." : "Skip"}
            </Button>
            
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm || isVoting}
              sx={{
                px: 6,
                py: 2,
                fontSize: 18,
                fontWeight: 700,
                borderRadius: 3,
                background: (!canConfirm || isVoting) 
                  ? "linear-gradient(135deg, #444 0%, #666 100%)"
                  : "linear-gradient(45deg, #10b981 30%, #34d399 90%)",
                color: "#fff",
                border: "none",
                boxShadow: (!canConfirm || isVoting)
                  ? "none"
                  : "0 4px 16px rgba(16, 185, 129, 0.3)",
                "&:hover": {
                  background: (!canConfirm || isVoting)
                    ? "linear-gradient(135deg, #444 0%, #666 100%)"
                    : "linear-gradient(45deg, #059669 30%, #10b981 90%)",
                  transform: (!canConfirm || isVoting) ? "none" : "translateY(-1px)",
                },
                "&:disabled": {
                  opacity: 0.7,
                },
                transition: "all 0.3s ease",
              }}
            >
              {isVoting ? "Voting..." : "Confirm Vote"}
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default CharacterVoting;